import React from 'react';
import { Element, Form } from './FormInfoBooking.styles';
import { Text } from '@/components/typography';
import TextField from '@/components/form/TextField/TextField';
import Fieldset from '@/components/form/Fieldset/Fieldset';
import Label from '@/components/form/Label/Label';
import { Button } from '@/components/button';
import FieldGroup from '@/components/form/FieldGroup/FieldGroup';
import TextArea from '@/components/form/Textarea/TextArea';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from '@/components/loading';
import { Banner } from '@/components/notification';
import dayjs from 'dayjs';
import Checkbox from '@/components/form/Checkbox/Checkbox';
import validationScheme, { FormInfoBookingDataType } from '@/features/forms/FormInfoBooking/validationScheme';
import PhoneIntel from '@/components/form/InputTel/InputTel';
import formPayloadParser, { FormPayloadDataType } from '@/features/forms/formPayloadParser';
import { FORMS_ENDPOINT } from '@/helpers/browser';
import FeedbackHandler from '@/features/forms/components/FeedbackHandler/FeedbackHandler';
import useFeedbackHandler from '@/features/forms/hooks/useFeedbackHandler';
import { trackFBPixel, trackGTMEvent, trackRDStation } from '@/utils/tracking';
import ucwords from '@/helpers/ucwords';
import { useTranslations } from 'next-intl';

export interface FormInfoBookingProps {
  productUuid?: string | number;
  productName?: 'pacote' | 'oferta' | 'cruzeiro';
  productTitle: string;
}

const FormInfoBooking = ({ productUuid, productName, productTitle }: FormInfoBookingProps) => {
  const t = useTranslations('FormInfoBooking');
  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { isValid, errors },
  } = useForm<FormInfoBookingDataType>({
    resolver: zodResolver(validationScheme),
  });

  const { feedback, submitting, submitted, submitRequestHandler } = useFeedbackHandler();

  const onSubmit = async (data: FormInfoBookingDataType) => {
    const merged = { productUuid, productName, productTitle, ...data } as unknown as FormPayloadDataType;
    const payload = formPayloadParser(merged);

    if (isValid) {
      submitRequestHandler(FORMS_ENDPOINT.form_info_booking(productName), payload).then((done) => {
        done && reset();
        // =====================
        // FORM EVENT TRACKING
        // =====================
        let formType: 'form-pacotes' | 'form-ofertas';
        switch (productName) {
          case 'pacote':
            formType = 'form-pacotes';
            break;
          case 'oferta':
            formType = 'form-ofertas';
            break;
          default:
            return;
        }
        trackGTMEvent({ eventCategory: 'Form', eventAction: 'submit', eventLabel: formType });
        trackRDStation(formType, payload);
        trackFBPixel(formType, `${ucwords(productName)} ${productTitle}`);
      });
    }
  };

  return (
    <Element>
      {/*added by: KTDEV-176*/}
      {submitted && (
        <Spinner>
          <Text>{t('Enviando sua mensagem, por favor, aguarde')}...</Text>
        </Spinner>
      )}
      {(submitting && (
        <Spinner>
          <Text>{t('Enviando sua mensagem, por favor, aguarde')}...</Text>
        </Spinner>
      )) || (
        <>
          <FeedbackHandler {...feedback} />
          <Banner
            show={Object.keys(errors).length > 0}
            appearance="error"
            message={t('Por favor, corrigir o(s) erro(s) abaixo:')}
          />
          {(!submitted && (
            <>
              <Text size="md" color="neutral_600">
                {t('Fale com um consultor!')}
              </Text>
              <Form
                action={`/proxy/action/form-info-booking-${productName}`}
                name="frmInfoBooking"
                id="frmInfoBooking"
                method="post"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <input type="hidden" data-privacy="true" name="privacy_policy" value="1" />
                <Fieldset>
                  <legend>{t('Seus dados')}</legend>
                  <FieldGroup>
                    <Label htmlFor="senderName" required>
                      {t('Seu nome')}
                    </Label>
                    <TextField id="name" {...register('name')} error={errors.name?.message} autoCapitalize="off" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label htmlFor="email" required>
                      {t('Seu email')}
                    </Label>
                    <TextField
                      id="email"
                      type="email"
                      {...register('email')}
                      error={errors.email?.message}
                      autoCapitalize="off"
                    />
                  </FieldGroup>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field }) => (
                      <PhoneIntel
                        label={`(Cod. ${t('Área')}) + ${t('Telefone')}`}
                        error={errors.phone?.message as any}
                        {...field}
                        setValue={setValue as (name: string, value: unknown, config?: Object) => void}
                      />
                    )}
                  />
                </Fieldset>

                <Fieldset>
                  <legend>{t('Detalhes sobre viagem')}</legend>
                  <FieldGroup>
                    <Label htmlFor="dateDepart" required>
                      {t('Data de embarque')}
                    </Label>
                    <TextField
                      type={'date'}
                      id="dateDepart"
                      {...register('dateDepart')}
                      error={errors.dateDepart?.message}
                      min={dayjs().add(7, 'days').format('YYYY-MM-DD')}
                      max={dayjs().add(2, 'years').format('YYYY-MM-DD')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label htmlFor="paxNumber" required>
                      {t('Quantidade de passageiros')}
                    </Label>
                    <TextField
                      id="paxNumber"
                      type="number"
                      {...register('paxNumber')}
                      error={errors.paxNumber?.message}
                      min={1}
                      max={30}
                      maxLenght={3}
                    />
                  </FieldGroup>
                </Fieldset>

                <Fieldset>
                  <legend>
                    {t('Mensagem')} <span>*</span>
                  </legend>
                  <FieldGroup>
                    <TextArea
                      placeholder={t('Escreva mais detalhes ou dúvidas que tiver sobre sua viagem')}
                      {...register('message')}
                      error={errors.message?.message}
                    />
                  </FieldGroup>
                </Fieldset>

                <Checkbox
                  label={t('Gostaria de receber ofertas exclusivas por email?')}
                  {...register('subscribeNews')}
                />

                <Button type="submit" block appearance="primary" disabled={submitting}>
                  {t('ENVIAR')}
                </Button>
              </Form>
            </>
          )) ||
            null}
          {/*added by: KTDEV-176*/}
          {/*<SubmittedButton setSubmitted={setSubmitted} resetFeedback={resetFeedback} />*/}
        </>
      )}
    </Element>
  );
};

export default FormInfoBooking;
